import { useEffect, useRef, useState } from 'react';
import { links, introductionMenu, secLoginMenu, todayInLondon } from './siteLinks';
import '../../Site.css';

function Dropdown({ label, items, align, outline }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`site-dropdown${align === 'right' ? ' site-dropdown--right' : ''}`} ref={ref}>
      <button
        type="button"
        className={`site-nav-button${outline ? ' site-nav-button--outline' : ''}`}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {label}
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <ul className="site-dropdown-menu">
          {items.map(item => (
            <li key={item.href}><a href={item.href}>{item.label}</a></li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Stripe({ thin }) {
  return (
    <div className={`site-stripe${thin ? ' site-stripe--thin' : ''}`} aria-hidden="true">
      <span /><span /><span /><span /><span />
    </div>
  );
}

export { Stripe };

// The notice bar's content (Web Admin → Notice bar; backend site_pages.py,
// slug 'notice'), fetched once per page load and shared by every header.
let noticeRequest = null;
function loadNotice() {
  if (!noticeRequest) {
    noticeRequest = fetch('/api/site/pages/notice')
      .then(res => (res.ok ? res.json() : null))
      .then(page => (page ? JSON.parse(page.body_md) : null))
      .catch(() => null);
  }
  return noticeRequest;
}

// An orange bar under the header while a notice is switched on and its
// "show until" day hasn't passed.
function NoticeBar() {
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    let live = true;
    loadNotice().then(n => { if (live) setNotice(n); });
    return () => { live = false; };
  }, []);
  if (!notice || !notice.show || !notice.text || (notice.until && notice.until < todayInLondon())) return null;
  const link = notice.link?.label && notice.link?.href ? notice.link : null;
  return (
    <div className="site-notice" role="status">
      <div className="site-wrap">
        <span>{notice.text}</span>
        {link && <a href={link.href}>{link.label}</a>}
      </div>
    </div>
  );
}

// The site header: the website's own pages (SiteLayout) and, via AppBar,
// every app page, so the menu (and the notice bar) are the same everywhere.
export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <>
      <header className="site-header">
        <div className="site-wrap">
          <a className="site-brand" href={links.home}>
            <img src="/site/logo-white.png" alt="" />
            <span className="site-brand-text">
              <span className="site-brand-name">NHCA</span>
              <span className="site-brand-sub">National Hill Climb Association</span>
            </span>
          </a>
          <button
            type="button"
            className="site-menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen(o => !o)}
          >
            <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {menuOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
          <nav id="site-nav" className={`site-nav${menuOpen ? ' site-nav--open' : ''}`} aria-label="Main">
            <Dropdown label="Introduction" items={introductionMenu} />
            <a className="site-nav-link" href={links.results}>Results</a>
            <a className="site-nav-link" href={links.photos}>Photos</a>
            <a className="site-nav-link" href={links.videos}>Videos</a>
            <a className="site-nav-link" href={links.calendar}>Calendar</a>
            <a className="site-nav-link" href={links.archives}>Archives</a>
            <a className="site-nav-link" href={links.contact}>Contact</a>
            <a className="site-nav-link site-nav-icon" href={links.facebook} target="_blank" rel="noreferrer" aria-label="NHCA on Facebook">
              <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
              <span className="site-nav-icon-label">Facebook</span>
            </a>
            <Dropdown label="Sec. login" items={secLoginMenu} align="right" outline />
          </nav>
        </div>
      </header>
      <NoticeBar />
    </>
  );
}

export default function SiteLayout({ children }) {
  return (
    <div className="site">
      <SiteHeader />

      <main className="site-main">{children}</main>

      <footer className="site-footer">
        <Stripe thin />
        <div className="site-wrap site-footer-cols">
          <div className="site-footer-about">
            <a className="site-brand" href={links.home}>
              <img src="/site/logo-white.png" alt="" />
              <span className="site-brand-name">NHCA</span>
            </a>
            <p>Motorcycle, sidecar and three-wheeler hill climbing at venues across England.</p>
            <div className="site-social">
              <a href={links.facebook} aria-label="NHCA on Facebook">
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
              </a>
              <a href={links.youtube} aria-label="NHCA on YouTube">
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="4" /><path d="M10 9l5 3-5 3z" /></svg>
              </a>
              <a href={links.photos} aria-label="NHCA photos">
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h3l2-3h6l2 3h3v13H4z" /><circle cx="12" cy="13" r="4" /></svg>
              </a>
            </div>
          </div>
          <div className="site-footer-col">
            <h2>Get started</h2>
            {introductionMenu.map(item => <a key={item.href} href={item.href}>{item.label}</a>)}
          </div>
          <div className="site-footer-col">
            <h2>Events</h2>
            <a href={links.calendar}>Calendar and entry</a>
            <a href={links.liveTiming}>Live timing</a>
            <a href={links.results}>Results</a>
            <a href={links.championship}>Championship</a>
            <a href={links.records}>Records</a>
            <a href={links.targetTimes}>Target times</a>
          </div>
          <div className="site-footer-col">
            <h2>The club</h2>
            <a href={links.contact}>Contact us</a>
            <a href={links.photos}>Photos</a>
            <a href={links.videos}>Videos</a>
            <a href={links.archives}>Archives</a>
            <a href={links.privacy}>Privacy statement</a>
          </div>
        </div>
        <div className="site-wrap site-footer-base">© National Hill Climb Association</div>
      </footer>
    </div>
  );
}
